import React, { useContext, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import useMenu from '../../hooks/useMenu';
import { DropDownContextProvider } from '../../contexts/DropDownContext';
import { FlexMiddle, FlexColumn, FlexCenter } from '../Flex';
import { card } from '../Card';
import { ReactComponent as BaseArrow } from '../../assets/arrow-right-small.svg';
import ConfigContext from '../../contexts/ConfigContext';
import { Search } from '..';
import DropDownItem from './DropDownItem';
import DropDownSeparator from './DropDownSeparator';

const Wrapper = styled.div.attrs(() => ({ className: 'drop-down-wrapper' }))`
  position: relative;

  &&& {
    ${({ $isOpen }) => $isOpen && 'display:block;'}
  }
`;

const Clickable = styled(FlexMiddle)`
  cursor: pointer;
  ${({ $isDisabled }) => $isDisabled && 'cursor: not-allowed;'}
`;

const SearchContainer = styled(FlexCenter)`
  position: sticky;
  top: 0;
  margin: 0 10px;
  background-color: ${({ theme }) => theme.colors.white};

  .drop-down-search {
    max-width: 100%;
    width: 100%;

    > div:last-child {
      max-width: 100%;
      width: 100%;
    }
  }
`;

const Arrow = styled(BaseArrow).attrs(() => ({ className: 'drop-down-arrow' }))`
  transform: rotate(${({ $isOpen }) => ($isOpen ? -90 : 90)}deg);
  width: 12px;
  margin-left: 7px;
  > path {
    fill: ${({ theme }) => theme.colors.white};
  }
`;

const NoOption = styled.div``;

const Options = styled(FlexColumn)`
  ${card};
  position: relative;
  text-align: left;
  border-radius: 8px;
  box-shadow: 0 6px 20px 0 rgba(0, 0, 0, 0.2);
  z-index: 5;
  gap: 3px;

  ${({ innerStyle }) => css`
    ${innerStyle}
  `}

  ${({ menuOffset: { top, right } = {} }) =>
    css`
      ${top !== undefined && `top: ${top}px;`}
      ${right !== undefined && `right: ${right}px;`}
    `}

  ${({ maxHeight }) =>
    maxHeight &&
    css`
      max-height: ${maxHeight}px;
      overflow: auto;
    `}


    padding: 10px;
  ${({ $hidePaddingTop }) =>
    $hidePaddingTop &&
    css`
      padding-top: 0px;
    `}

  min-width: ${({ menuMinWidth }) => menuMinWidth}px;
  max-width: ${({ menuMaxWidth }) => menuMaxWidth}px;

  .drop-down-no-options {
    padding: 8px;
  }
`;

const DropDown = ({
  className,
  portalStyle,
  children,
  renderValue,
  menuOffset,
  menuMinWidth = 90,
  showArrow = true,
  clickableContainer: ClickableContainer,
  scrollingContainer,
  usePortal,
  controlledIsOpen = false,
  openFromRight = true,
  maxHeight,
  menuMaxWidth,
  showSearch = false,
  isMenuTriggerWidth = false,
  isDisabled = false,
  renderNextToSearchItems: RenderNextToSearchItems = React.Fragment
}) => {
  const { t } = useContext(ConfigContext);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);

  const { Portal, wrapperRef, isOpen: isMenuOpen, setToggle, setClose, style, menuRef } = useMenu({
    usePortal,
    openFromRight,
    menuOffset,
    scrollingContainer
  });

  const isOpen = useMemo(() => controlledIsOpen || isMenuOpen, [controlledIsOpen, isMenuOpen]);

  const Container = ClickableContainer ?? Clickable;

  const filteredChildren = React.Children.toArray(children)
    .filter((child) => child.props.isVisible === undefined || child.props.isVisible)
    .filter((child) => {
      if (!showSearch) {
        // no search enabled, return all
        return true;
      }
      if (child.props.rawSearchLabel) {
        // specific search label was provided, search according to it
        return child.props.rawSearchLabel.toLowerCase().includes(search.toLowerCase());
      }
      if (typeof child.props.children !== 'string') {
        // search is enabled, but children prop is not a string, return all
        return true;
      }
      // search by children string value
      return child.props.children.toLowerCase().includes(search.toLowerCase());
    });

  const clickHandlers = useMemo(() => {
    const result = {};

    result.onClick = (e) => {
      if (isDisabled) {
        return;
      }

      return setToggle(e);
    };

    return result;
  }, [setToggle, isDisabled]);

  return (
    <DropDownContextProvider value={{ isOpen, closeMenu: setClose }}>
      <Wrapper className={className} ref={wrapperRef} $isOpen={isOpen}>
        <Container
          ref={containerRef}
          onTouchStart={clickHandlers?.onTouchStart}
          onClick={clickHandlers?.onClick}
          $isDisabled={isDisabled}
          isActive={isOpen}>
          {React.isValidElement(renderValue)
            ? React.cloneElement(renderValue, { isactive: { isOpen: isOpen.toString() } })
            : typeof renderValue === 'function'
            ? renderValue({ isOpen })
            : renderValue}
          {showArrow && <Arrow $isOpen={isOpen} />}
        </Container>

        {isOpen && (
          <Portal>
            <Options
              $hidePaddingTop={showSearch}
              ref={menuRef}
              innerStyle={{ ...style, ...portalStyle }}
              // added isMenuTriggerWidth to set menuMinWidth to be the same as the style?.minWidth
              // since style from useMenu is not applied to the menu and always returns trigger width
              menuMinWidth={isMenuTriggerWidth ? style?.minWidth : menuMinWidth}
              maxHeight={maxHeight}
              menuMaxWidth={menuMaxWidth}>
              {showSearch && (
                <SearchContainer menuMinWidth={menuMinWidth} maxHeight={maxHeight}>
                  <Search
                    className="drop-down-search"
                    placeholder={t('search')}
                    value={search}
                    onChange={setSearch}
                    keepOpen
                    autoFocus
                  />
                  <RenderNextToSearchItems onClose={(e) => setToggle(e)} />
                </SearchContainer>
              )}

              {showSearch && filteredChildren.length === 0 && (
                <NoOption className="drop-down-no-options">{t('noOptions')}</NoOption>
              )}

              {filteredChildren}
            </Options>
          </Portal>
        )}
      </Wrapper>
    </DropDownContextProvider>
  );
};

DropDown.Item = DropDownItem;
DropDown.Separator = DropDownSeparator;

export default DropDown;
